import { useMemo, useEffect } from 'react';

import classNames from 'classnames';
import MUIDialog, { DialogProps as MUIDialogProps } from '@mui/material/Dialog';

import { useIsInsideContainer } from '../hooks';

import { useStyles } from './Dialog.styles';

/**
 * Свойства компонента {@link Dialog}
 */
export interface DialogProps extends MUIDialogProps {
  /**
   * Если true, диалог не будет иметь закруглённых углов.
   */
  withoutRadius?: boolean;

  /**
   * Если true, диалог будет полноэкранным на мобильных устройствах.
   *
   * @defaultValue `false`
   */
  fullScreen?: boolean;
  width?: number;
  bottomIslandSpace?: boolean;
}

const SCROLLABLE_DIALOG_ID = 'scrollable-dialog';

/**
 * Компонент диалога, который расширяет диалоговый компонент Material-UI дополнительными опциями стилизации.
 *
 * @public
 * @param fullScreen - Если true, диалог будет полноэкранным на мобильных устройствах.
 * @param withoutRadius - Если true, диалог не будет иметь закруглённых углов.
 * @param width - Применяется если передан.
 * @param children - Содержимое, которое будет отображаться внутри диалога.
 * @param classes - Пользовательские CSS-классы для переопределения стилей по умолчанию.
 * @param open - Если true, диалог открыт.
 * @param bottomIslandSpace - Если true, снизу резервируется место под островок действий.
 * @param props - Дополнительные свойства, которые можно передать в компонент Dialog Material-UI.
 * @returns  JSX-элемент, представляющий диалог, если он открыт, в противном случае null.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link DialogProps}
 */

export const Dialog = ({
  fullScreen = false,
  withoutRadius,
  width,
  bottomIslandSpace,
  children,
  classes,
  open,
  ...props
}: DialogProps) => {
  const { classes: styles } = useStyles({ width });
  const isInsideContainer = useIsInsideContainer();

  const modalClasses = useMemo(
    () => ({
      ...classes,
      container: classNames(styles.dialogContainer, classes?.container),
      paper: classNames(
        styles.dialogPaper,
        classes?.paper,
        withoutRadius && styles.dialogWithoutRadius,
        bottomIslandSpace && styles.bottomIslandSpace,
      ),
    }),
    [classes, styles, withoutRadius, bottomIslandSpace],
  );

  useEffect(() => {
    if (isInsideContainer) {
      document.body.style.overflow = open ? 'hidden' : '';
    }

    return () => {
      if (isInsideContainer) {
        document.body.style.overflow = '';
      }
    };
  }, [open, isInsideContainer]);

  if (open) {
    return (
      <MUIDialog
        classes={modalClasses}
        fullScreen={fullScreen}
        className={classNames('aqa_dialog', props.className)}
        open={open}
        data-testid="dialog-test-id"
        PaperProps={{
          id: SCROLLABLE_DIALOG_ID,
        }}
        {...props}
      >
        {children}
      </MUIDialog>
    );
  }

  return null;
};
